/**
 * Rules engine - evaluator.
 *
 * Order:
 *   1. Filter rules to the given trigger moment
 *   2. Keep only enabled rules
 *   3. Sort by priority (ascending — lower runs first)
 *   4. Return the first match's action (send or skip)
 *   5. If nothing matched, return 'no_match'
 *
 * Semantics:
 *   - Explicit `skip` outranks later `send` because it has lower priority
 *   - If an admin wants "send everything except X", they put a `skip` rule
 *     at priority 1 matching X, then a catch-all `send` at priority 100
 */

import type { TriggerMoment } from '../types';
import type { Rule, RuleDecision, StudyFacts, StudyMatcher } from './types';

export function evaluateRules(
  rules: Rule[],
  moment: TriggerMoment,
  facts: StudyFacts,
): RuleDecision {
  const applicable = rules
    .filter((r) => r.enabled && r.when === moment)
    .sort((a, b) => a.priority - b.priority);

  for (const rule of applicable) {
    if (matchesStudy(rule.match, facts)) {
      return { matchedRule: rule, action: rule.action };
    }
  }
  return { matchedRule: null, action: 'no_match' };
}

export function matchesStudy(matcher: StudyMatcher, facts: StudyFacts): boolean {
  const now = facts.now ?? new Date();

  if (matcher.modality && !inList(facts.modality, matcher.modality)) return false;
  if (matcher.bodyPartContains && !containsAny(facts.bodyPart, matcher.bodyPartContains)) {
    return false;
  }
  if (matcher.sendingAe && !inList(facts.sendingAe, matcher.sendingAe)) return false;
  if (matcher.institution && !inList(facts.institution, matcher.institution)) return false;
  if (matcher.referringPhysician && !inList(facts.referringPhysician, matcher.referringPhysician)) {
    return false;
  }
  if (matcher.priorityTag && facts.priorityTag !== matcher.priorityTag) return false;

  if (matcher.ageMin != null) {
    if (facts.patientAgeYears == null || facts.patientAgeYears < matcher.ageMin) return false;
  }
  if (matcher.ageMax != null) {
    if (facts.patientAgeYears == null || facts.patientAgeYears > matcher.ageMax) return false;
  }

  if (matcher.daysOfWeek && matcher.daysOfWeek.length > 0) {
    const dayName = (['Sun','Mon','Tue','Wed','Thu','Fri','Sat'] as const)[now.getDay()];
    if (!matcher.daysOfWeek.includes(dayName)) return false;
  }

  if (matcher.timeWindow) {
    if (!isInTimeWindow(now, matcher.timeWindow)) return false;
  }

  if (matcher.studyDescriptionRegex) {
    try {
      const re = new RegExp(matcher.studyDescriptionRegex, 'i');
      if (!re.test(facts.studyDescription ?? '')) return false;
    } catch {
      // Bad regex from admin UI — treat as non-matching rather than throw,
      // so one bad rule can't block the whole evaluator.
      return false;
    }
  }

  return true;
}

function inList(value: string | undefined, allowed: string | string[]): boolean {
  if (!value) return false;
  const arr = Array.isArray(allowed) ? allowed : [allowed];
  return arr.some((a) => a.toUpperCase() === value.toUpperCase());
}

function containsAny(value: string | undefined, needles: string | string[]): boolean {
  if (!value) return false;
  const arr = Array.isArray(needles) ? needles : [needles];
  const up = value.toUpperCase();
  return arr.some((n) => up.includes(n.toUpperCase()));
}

/** 'HH:MM-HH:MM' local time. Supports wrap-around ('22:00-06:00'). */
function isInTimeWindow(now: Date, window: string): boolean {
  const m = /^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/.exec(window);
  if (!m) return false;
  const sh = +m[1], sm = +m[2], eh = +m[3], em = +m[4];
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  if (startMin <= endMin) {
    return nowMin >= startMin && nowMin <= endMin;
  }
  // Wraps midnight (e.g. 22:00-06:00).
  return nowMin >= startMin || nowMin <= endMin;
}
